import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  usePrevious,
} from "@chakra-ui/react";
import { BatchItem } from "@rpldy/uploady";
import { parseISO } from "date-fns";
import { useFeePostingTimeRangesList } from "gen/endpoints/charge/charge";
import {
  documentStatusUploadCompleteUpdate,
  useDocumentRetrieve,
} from "gen/endpoints/documents/documents";
import { useFeeChargeEventCreate } from "gen/endpoints/fee-charge-event/fee-charge-event";
import {
  useFeeBasisUploadTemplateRetrieve,
  useFeeListSuspense,
} from "gen/endpoints/fees/fees";
import { DocumentCreateResponseData } from "gen/model/documentCreateResponseData";
import { DocumentStateMachineFieldsEnum } from "gen/model/documentStateMachineFieldsEnum";
import { DocumentTypeEnum } from "gen/model/documentTypeEnum";
import { FeeChargeTimingEnum } from "gen/model/feeChargeTimingEnum";
import { FeeData } from "gen/model/feeData";
import { get, isUndefined, map } from "lodash-es";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import { LoadingState } from "shared/components/empty/LoadingState";
import Form from "shared/components/form/Form";
import { Select } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { ConfirmationDialogProps } from "shared/components/modal/confirm/ConfirmationDialog";
import { useToast } from "shared/components/toast/useToast";
import { FileUpload } from "shared/components/upload/FileUpload";
import { FileUploadHandle } from "shared/components/upload/FileUploadController";
import { uploadAcceptCsvAndExcel } from "shared/components/upload/FileUploadUtils";
import { WarningIcon } from "shared/icons/WarningIcon";
import { formatIsoDateToSummary } from "shared/utils/DateUtils";
import { downloadFileBlob } from "shared/utils/utils";

export interface PostFeesModalData {
  feeChargeEventId: string;
}

export interface PostFeesModalProps {
  fundFamilyId: string;
  lastChargedPeriod?: string;
}

export const PostFeesModal: React.FC<
  ConfirmationDialogProps<PostFeesModalData, PostFeesModalProps>
> = ({ alertProps, ...props }) => {
  const { t } = useTranslation(["fees"]);
  return (
    <Modal scrollBehavior="inside" {...alertProps}>
      <ModalOverlay />
      <ModalContent>
        <QueryBoundary
          pendingFallback={
            <>
              <ModalHeader>
                {t("fees:create.title")}
                <ModalCloseButton />
              </ModalHeader>
              <ModalBody>
                <LoadingState />
              </ModalBody>
            </>
          }
        >
          <PostFeesModalContent {...props} />
        </QueryBoundary>
      </ModalContent>
    </Modal>
  );
};

const PostFeesModalContent: React.FC<
  Omit<
    ConfirmationDialogProps<PostFeesModalData, PostFeesModalProps>,
    "alertProps"
  >
> = ({
  onSuccess,
  onCancel,
  extraProps: { fundFamilyId, lastChargedPeriod },
}) => {
  const { t } = useTranslation(["common", "fees"]);
  const { showErrorToast } = useToast();

  const handleRef = useRef<FileUploadHandle>(null);
  const [documentId, setDocumentId] = useState<string | undefined>(undefined);
  const [feeChargeEventId, setFeeChargeEventId] = useState<string | undefined>(
    undefined,
  );

  const { data: feesData } = useFeeListSuspense(fundFamilyId);

  const [selectedFee, setSelectedFee] = useState<FeeData | undefined>(
    feesData[0],
  );

  const [canUpload, setCanUpload] = useState(false);

  const { data: documentData } = useDocumentRetrieve(documentId!, {
    query: {
      enabled: !!documentId,
      refetchInterval: 1000,
    },
  });

  const { data: suggestedTimeRangeData } = useFeePostingTimeRangesList(
    fundFamilyId,
    selectedFee?.id || "",
    { option_count: 1 },
    {
      query: {
        enabled: !!selectedFee,
      },
    },
  );

  const selectedTimeRange = suggestedTimeRangeData?.[0];
  const feeFullyCharged =
    suggestedTimeRangeData && suggestedTimeRangeData.length === 0;

  const { data: excelData } = useFeeBasisUploadTemplateRetrieve(
    fundFamilyId,
    selectedFee?.id || "",
    {
      from_date: selectedTimeRange?.charged_date_lower || "",
      to_date: selectedTimeRange?.charged_date_upper || "",
    },
    {
      query: {
        enabled: Boolean(selectedFee?.id) && Boolean(selectedTimeRange),
      },
    },
  );

  const { mutateAsync: feeChargeEventCreate, isSuccess } =
    useFeeChargeEventCreate();

  const findAndSelectFee = (value: string) => {
    const fee = feesData.find((fee) => fee.id === value);
    setSelectedFee(fee);
  };

  const onItemFinished = async (item: BatchItem) => {
    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
    const document: DocumentCreateResponseData = get(item, "document")!;
    setDocumentId(document.id);
    await documentStatusUploadCompleteUpdate(document.id);
  };

  const onItemsChanged = useCallback((files: BatchItem[]) => {
    setCanUpload(files.length === 0);
  }, []);

  const canSubmit =
    (selectedTimeRange?.is_upload_required && documentData?.id) ||
    (selectedTimeRange?.is_upload_required == false && selectedTimeRange);

  const onClick = () => {
    downloadFileBlob(
      excelData!,
      t("fees:download.uploadFilename", {
        fee_name: selectedFee?.name.replace(" ", "_"),
        quarter: selectedTimeRange?.option.rich_value.replace(" ", "_"),
      }),
    );
  };

  const firstDayOfCurrentPeriod = selectedTimeRange?.charged_date_lower;
  const firstDayOfNextPeriod =
    selectedTimeRange && parseISO(selectedTimeRange.charged_date_upper);
  const lastDayOfCurrentPeriod =
    firstDayOfNextPeriod &&
    new Date(
      firstDayOfNextPeriod.setDate(firstDayOfNextPeriod.getDate() - 1),
    ).toISOString();
  const effectiveDate =
    firstDayOfCurrentPeriod &&
    lastDayOfCurrentPeriod &&
    formatIsoDateToSummary(
      selectedFee?.charge_timing === FeeChargeTimingEnum.in_advance
        ? firstDayOfCurrentPeriod
        : lastDayOfCurrentPeriod,
    );

  const onSubmit = async () => {
    const feeChargeEvent = await feeChargeEventCreate({
      fundFamilyId,
      data: {
        fund_family_id: fundFamilyId,
        fee_id: selectedFee!.id,
        charged_date_lower: selectedTimeRange?.charged_date_lower ?? "",
        fee_basis_upload_document_id: documentId,
      },
    });
    const { id: feeChargeEventId } = feeChargeEvent;
    if (!feeChargeEventId) {
      return;
    }
    setFeeChargeEventId(feeChargeEventId);
    if (isUndefined(documentId)) {
      onSuccess({ feeChargeEventId });
    }
  };

  const form = useForm();

  const prevState = usePrevious(documentData?.state);

  useEffect(() => {
    if (documentData && prevState !== documentData.state) {
      if (
        documentData.state ===
          DocumentStateMachineFieldsEnum.successful_ingestion &&
        feeChargeEventId
      ) {
        onSuccess({ feeChargeEventId });
      } else if (
        documentData.state === DocumentStateMachineFieldsEnum.failed_ingestion
      ) {
        handleRef.current?.clear();
        showErrorToast(documentData.error);
        setDocumentId(undefined);
      }
    }
  }, [documentData, feeChargeEventId, onSuccess, prevState, showErrorToast]);

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      // keep form disabled after success while navigating
      isDisabled={isSuccess}
    >
      <ModalHeader>
        {t("fees:create.title")}
        <ModalCloseButton />
      </ModalHeader>
      <ModalBody>
        <Select
          title={t("fees:create.fee")}
          value={selectedFee?.id || ""}
          onChangeValue={findAndSelectFee}
          isReadOnly={feesData.length === 1}
        >
          {feesData.map((fee) => (
            <SelectOption key={fee.id} value={fee.id}>
              {fee.name}
            </SelectOption>
          ))}
        </Select>
        {suggestedTimeRangeData && suggestedTimeRangeData.length > 0 && (
          <Select
            title={t("fees:create.availableTimeRange")}
            elements={map(suggestedTimeRangeData, (data) => data.option)}
            value={selectedTimeRange?.option.value || ""}
            isReadOnly={true}
          />
        )}
        {selectedTimeRange?.is_upload_required && (
          <>
            <Text mb="16px">{t("fees:download.notes")}</Text>
            <Text mb="16px">
              <Trans ns="fees" i18nKey="download.excelTemplate">
                To start, download this
                <Link onClick={onClick}>excel template</Link>
              </Trans>
            </Text>
            <Text mb="4px">{t("fees:download.completion")}</Text>
            <Box mb="16px">
              <FileUpload
                documentType={DocumentTypeEnum.management_fee_basis}
                accept={uploadAcceptCsvAndExcel}
                onItemFinished={onItemFinished}
                onItemsChanged={onItemsChanged}
                canUpload={canUpload}
                handleRef={handleRef}
              />
            </Box>
          </>
        )}
        {feeFullyCharged && (
          <Alert status="warning" data-ls-enabled={true} data-public={true}>
            <AlertIcon>
              <WarningIcon />
            </AlertIcon>
            <AlertDescription>
              {t("fees:postFees.feeExhausted", {
                rich_name_of_period: lastChargedPeriod,
              })}
            </AlertDescription>
          </Alert>
        )}
        {selectedFee?.charge_timing && effectiveDate && (
          <Box borderTopWidth="1px" paddingTop="16px" alignItems="flex-start">
            <Trans
              ns="fees"
              i18nKey="create.info"
              values={{
                charge_timing: t(
                  `fees:chargeTiming.${selectedFee.charge_timing}`,
                ),
                effective_date: effectiveDate,
              }}
              components={[
                <Text key="effectiveDateSpan" as="span" fontWeight="500" />,
              ]}
            />
          </Box>
        )}
      </ModalBody>
      <ModalFooter gap={2}>
        <ButtonOrder
          dismiss={<FormCancelButton onClick={() => onCancel()} />}
          confirm={
            <FormSubmitButton isDisabled={!canSubmit}>
              {t("fees:create.continueToReviewBasis")}
            </FormSubmitButton>
          }
        />
      </ModalFooter>
    </Form>
  );
};
